import { showError, showSuccess } from '../../shared/ui/appToast';
import { supabase } from '../../shared/supabase/supabaseClient';
import { getOAuthRedirectUri } from './sso';
import { useAuthStore } from '../store/useAuthStore';
import { logAuditEvent } from '../../shell/audit/auditLogs';
import { buildClientAuditMeta } from '../../shell/clientPresence/buildClientPresencePayload';
import { clearPasswordRecoveryFlowMarker } from './passwordRecoveryWebStorage';
import { userFacingSupabaseAuthMessage } from './authMessages';
import { normalizeEmail } from './authFormValidation';
import { markAuthErrorHandled, reportAuthFailure } from './authObservability';

/** `pending_email_confirmation`: Auth no devolvió sesión, hay que confirmar el correo. */
export type RegisterOutcome = 'signed_in' | 'pending_email_confirmation';

export const registerWithEmail = async (email: string, password: string): Promise<RegisterOutcome> => {
  const authStore = useAuthStore.getState();
  authStore.setLoading(true);
  authStore.setError(undefined);
  try {
    const normalized = normalizeEmail(email);
    const { data: idCheck, error: idErr } = await supabase.rpc('check_identity_allowed', {
      p_kind: 'email_norm',
      p_raw_value: normalized,
    });
    if (idErr) {
      // ponytail: fail-closed ante error RPC — no permitir signup si no podemos verificar identidad
      console.error('[registerWithEmail] identity check failed', idErr.message);
      const message = 'No pudimos verificar tus datos. Intentá de nuevo en unos minutos.';
      authStore.setError(message);
      showError('No pudimos registrar la cuenta', message);
      // RPC roto = funnel de registro muerto → siempre issue en Sentry.
      reportAuthFailure('signup.identity_check', idErr, { title: 'No pudimos registrar la cuenta' });
      throw markAuthErrorHandled(new Error(message));
    }
    const idPayload = idCheck as { ok?: boolean; error?: string } | null;
    if (idPayload?.ok === false && idPayload.error === 'identity_blocked') {
      const message = 'No podemos crear la cuenta con estos datos.';
      authStore.setError(message);
      showError('No pudimos registrar la cuenta', message);
      const blocked = new Error(message) as Error & { code?: string };
      blocked.code = 'identity_blocked';
      reportAuthFailure('signup', blocked);
      throw markAuthErrorHandled(blocked);
    }

    const redirectTo = getOAuthRedirectUri();
    // Intención Crece pre-signup vive en sessionStorage/efímero (captureMarketingPlanIntent).
    // No escribir pending_plan en metadata acá: eso marcaba “pago sin terminar” sin haber ido a MP.
    const { data, error } = await supabase.auth.signUp({
      email: normalized,
      password,
      options: {
        emailRedirectTo: redirectTo,
      },
    });
    if (error || !data) {
      throw error ?? new Error('No pudimos crear la cuenta.');
    }
    if (data.session && data.user) {
      clearPasswordRecoveryFlowMarker();
      authStore.setSession(data.session, data.user);
      await logAuditEvent('register.success', { email: normalized });
      showSuccess('Cuenta creada', 'Ya podés configurar tu negocio.');
      return 'signed_in';
    }
    return 'pending_email_confirmation';
  } catch (err: any) {
    const message = userFacingSupabaseAuthMessage(err?.message ?? '', 'signup', {
      code: err?.code,
      status: err?.status,
    });
    authStore.setError(message);
    showError('No pudimos registrar la cuenta', message);
    reportAuthFailure('signup', err);
    throw err;
  } finally {
    authStore.setLoading(false);
  }
};

/** Reenvía el mail de confirmación de alta. */
export const resendSignupConfirmation = async (email: string): Promise<void> => {
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email: normalizeEmail(email),
    options: { emailRedirectTo: getOAuthRedirectUri() },
  });
  if (error) {
    reportAuthFailure('resend', error);
    throw new Error(
      userFacingSupabaseAuthMessage(error.message ?? '', 'signup', {
        code: (error as { code?: string }).code,
        status: error.status,
      }),
    );
  }
};

export const loginWithEmail = async (email: string, password: string) => {
  const authStore = useAuthStore.getState();
  authStore.setLoading(true);
  authStore.setError(undefined);
  try {
    const normalized = normalizeEmail(email);
    const { data, error } = await supabase.auth.signInWithPassword({ email: normalized, password });
    if (error || !data.session) {
      throw error ?? new Error('No pudimos iniciar sesión.');
    }
    clearPasswordRecoveryFlowMarker();
    authStore.setSession(data.session, data.session.user ?? null);
    const client = await buildClientAuditMeta();
    await logAuditEvent('login.success', { email: normalized, client });
  } catch (err: any) {
    if (err?.code === 'email_not_confirmed') {
      authStore.setError(undefined);
      reportAuthFailure('login', err);
      throw err;
    }
    const message = userFacingSupabaseAuthMessage(err?.message ?? '', 'login', {
      code: err?.code,
      status: err?.status,
    });
    authStore.setError(message);
    showError('No pudimos iniciar sesión', message);
    reportAuthFailure('login', err);
    throw err;
  } finally {
    authStore.setLoading(false);
  }
};
